<script setup lang="ts">
import {
  type Component,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  useTemplateRef,
  type VNode,
} from "vue";
import { MenuEntry } from "../../models/menu-entry";
import BcMenu from "./bc-menu.vue";

export type BcContextMenuProps = {
  subItemIcon?: VNode | Component;
  separateAll?: boolean;
  getItems?: (event: MouseEvent) => MenuEntry[];
  containerId?: string;
};

export type BcContextMenuEmits = {
  (event: "contextmenu"): void;
  (event: "customevent", msg: string): void;
};

const emit = defineEmits<BcContextMenuEmits>();

const menuRef = useTemplateRef("menuRef");
const items = shallowRef<MenuEntry[]>([]);
const x = ref(0);
const y = ref(0);
const isVisible = ref(false);
let container = document.body;
let menuElement: HTMLElement | undefined;

const {
  subItemIcon,
  separateAll = false,
  getItems,
  containerId,
} = defineProps<BcContextMenuProps>();

onMounted(() => {
  container = document.getElementById(containerId ?? "") ?? document.body;
  menuElement = menuRef.value?.$el;
  if (menuElement) {
    container.appendChild(menuElement);
  }
  container.addEventListener("contextmenu", onContainerContextMenu, { capture: true });
  container.addEventListener("keydown", onContainerKeypress, { capture: true });
  document.addEventListener("click", hideContextMenuOnEvent, { capture: true });
  document.addEventListener("scroll", hideContextMenuOnEvent, { capture: true });
  window.addEventListener("resize", hideContextMenu, { capture: true });
});

onUnmounted(() => {
  container.removeEventListener("contextmenu", onContainerContextMenu, { capture: true });
  container.addEventListener("keydown", onContainerKeypress, { capture: true });
  document.removeEventListener("click", hideContextMenuOnEvent, { capture: true });
  document.removeEventListener("scroll", hideContextMenuOnEvent, { capture: true });
  window.removeEventListener("resize", hideContextMenu, { capture: true });
  menuElement?.remove();
});

function hideContextMenu(): void {
  isVisible.value = false;
}

function hideContextMenuOnEvent(event: Event): void {
  let contextMenuDiv = menuRef.value?.$el;
  if (
    event.target !== contextMenuDiv &&
    !contextMenuDiv?.contains(event.target as Node)
  ) {
    isVisible.value = false;
  }
}

function onContainerKeypress(event: KeyboardEvent): void {
  if (event.key === "Escape" || event.key === "Esc") {
    hideContextMenuOnEvent(event);
  }
}

function onContainerContextMenu(event: MouseEvent): void {
  const el = getClosestElement(event.target);

  const isTextTarget =
    !!el &&
    !!el.closest(
      'textarea, input:not([type]), input[type="text"], input[type="email"], input[type="number"], input[type="search"]',
    );

  if (!isTextTarget) {
    event.preventDefault();
  }

  if (!menuRef.value || menuRef.value.$el?.contains(event.target as Node)) {
    return;
  }

  items.value = getItems ? getItems(event) : [];
  x.value = event.x;
  y.value = event.y;
  isVisible.value = !!items.value && items.value.length > 0;
  emit("contextmenu");
}

function getClosestElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

defineExpose({
  hideContextMenu,
});
</script>

<template>
  <BcMenu
    ref="menuRef"
    class="contextMenu"
    v-bind:separateAll="separateAll"
    v-bind:subItemIcon="subItemIcon"
    v-bind:isVisible="isVisible"
    v-bind:x="x"
    v-bind:y="y"
    v-bind:items="items"
  />
</template>
